// HRM_CLEANUP: Delete this entire file and ConfidentialityStatementPopup.module.scss when Ontario MD certification is complete
import { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";

import { ISelectControlOption, SelectControl } from "@/Common/Components/Inputs/SelectControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { Popup } from "@/Common/Components/Modals/Popup";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { NumberInputWithDecimals } from "@/Flowsheets/Components/ConfigureFlowsheetModal/CareElementsConfigurationParts/NumberInputWIthDecimals";
import { OntarioMdHealthReportManagerApiClient } from "../../Api/OntarioMdHealthReportManagerApiClient";

import styles from "./ConfidentialityStatementPopup.module.scss";

interface IConfidentialityStatementPopupProps {
    isOpen: boolean;
    onClose: () => void;
}

type IntervalType = "Minutes" | "Hours";

const minutesRegex = /^(?:[1-9]|[1-5]\d)?$/;
const hoursRegex = /^(?:[1-9]|1\d|2[0-4])?$/;

function ConfidentialityStatementPopup(props: IConfidentialityStatementPopupProps): JSX.Element {
    const { getText } = useTranslation();
    const [confidentialityStatement, setConfidentialityStatement] = useState<string>("");
    const [intervalValue, setIntervalValue] = useState<string>("");
    const [intervalType, setIntervalType] = useState<IntervalType>("Minutes");
    const [isLoaded, setIsLoaded] = useState(false);

    const intervalOptions: ISelectControlOption<IntervalType>[] = [
        { label: "Minutes", value: "Minutes" },
        { label: "Hours", value: "Hours" }
    ];

    const parseInterval = useCallback(
        (intervalFormat: string | null | undefined): { value: string; type: IntervalType } => {
            if (!intervalFormat) {
                return { value: "", type: "Minutes" };
            }

            const parts = intervalFormat.split(":");
            if (parts.length !== 3) {
                return { value: "", type: "Minutes" };
            }

            const [hoursStr, minutesStr] = parts;
            const hours = Number.parseInt(hoursStr, 10);
            const minutes = Number.parseInt(minutesStr, 10);

            if (Number.isNaN(hours) || Number.isNaN(minutes)) {
                return { value: "", type: "Minutes" };
            }

            if (hours > 0 && minutes === 0) {
                return { value: hours.toString(), type: "Hours" };
            }

            if (minutes > 0 && hours === 0) {
                return { value: minutes.toString(), type: "Minutes" };
            }

            return { value: "", type: "Minutes" };
        },
        []
    );

    const formatIntervalForApi = useCallback((value: string, type: IntervalType): string => {
        const numValue = Number.parseInt(value, 10);
        if (Number.isNaN(numValue) || numValue < 0) return "00:00:00";

        if (type === "Hours") {
            return `${numValue.toString().padStart(2, "0")}:00:00`;
        }
        else {
            return `00:${numValue.toString().padStart(2, "0")}:00`;
        }
    }, []);

    const handleIntervalTypeChange = (type: IntervalType): void => {
        setIntervalType(type);
        setIntervalValue("");
    };

    const handleConfidentialityStatement = useCallback(
        (val: string) => setConfidentialityStatement(val),
        []
    );

    const loadData = useCallback(async (): Promise<void> => {
        setIsLoaded(false);
        try {
            const response = await OntarioMdHealthReportManagerApiClient.getConfidentialityStatement();
            if (response.isSuccess) {
                const statement = response.data?.ConfidentialityStatement ?? "";
                setConfidentialityStatement(statement);

                const parsed = parseInterval(response.data?.Interval || "");
                setIntervalValue(parsed.value);
                setIntervalType(parsed.type);
            }
            else {
                setConfidentialityStatement("");
                setIntervalValue("");
            }
        }
        catch (err) {
            console.error("Error loading data:", err);
        }
        finally {
            setIsLoaded(true);
        }
    }, [parseInterval]);

    useEffect(() => {
        if (props.isOpen) loadData();
        else {
            setIsLoaded(false);
        }
    }, [props.isOpen, loadData]);

    const handleSave = async (): Promise<void> => {
        try {
            await OntarioMdHealthReportManagerApiClient.updateConfidentialityStatement(
                confidentialityStatement,
                formatIntervalForApi(intervalValue, intervalType)
            );

            props.onClose();
        }
        catch (error) {
            console.error("Error saving data:", error);
        }
    };

    const handleCancel = (): void => props.onClose();

    return props.isOpen ? ReactDOM.createPortal(
        <Popup
            cssClass="centered-popup width-800"
            title="Confidentiality Statement & Interval Settings"
            onCloseDelegate={props.onClose}
            standardFooterProps={{
                okButton: { label: getText("SaveButton"), onClick: () => { handleSave(); } },
                cancelButton: { label: getText("CancelLabel"), onClick: handleCancel }
            }}
        >
            <div className={styles.container}>
                <div className={styles.intervalSection}>
                    <label htmlFor="interval-input" className={styles.fieldLabel}>
                        Automatic pulling interval
                    </label>
                    {isLoaded ? (
                        <div className={styles.inputWithLabel}>
                            <NumberInputWithDecimals
                                key={intervalType}
                                numberValue={intervalValue === "" ? null : Number(intervalValue)}
                                onChange={rawValue => {
                                    setIntervalValue(rawValue ?? "");
                                }}
                                regex={intervalType === "Minutes" ? minutesRegex : hoursRegex}
                                dataCy="interval-input"
                            />
                            <div className={styles.selectWrapper}>
                                <SelectControl
                                    options={intervalOptions}
                                    value={intervalType}
                                    onChange={handleIntervalTypeChange}
                                />
                            </div>
                        </div>
                    ) : (
                        <div aria-busy="true" />
                    )
                    }
                </div>

                <div>
                    <label htmlFor="confidentiality-textarea" className={styles.fieldLabel}>
                        Confidentiality Statement
                    </label>

                    {isLoaded ? (
                        <TextAreaResizable
                            id="confidentiality-textarea"
                            text={confidentialityStatement}
                            onChange={handleConfidentialityStatement}
                            rows={8}
                            minHeightPx={150}
                            maxLength={1000}
                        />
                    ) : (
                        <div aria-busy="true" />
                    )
                    }
                </div>
            </div>
        </Popup>,
        document.body
    ) : null;
}

export { ConfidentialityStatementPopup };
